/**
 * Centralized membership plans data
 * Used by: MembershipPage (protected), PublicMembershipPage (public /pricing)
 * Update this file to change plan content everywhere.
 *
 * @author Varun Pandey
 */

import type React from 'react';
import {
  Upload,
  Clock,
  Phone,
  TrendingUp,
  Shield,
  Coins,
} from 'lucide-react';
import { isNativeIOSApp } from '@/utils/platform';

export interface MajorFeature {
  icon: React.ElementType;
  label: string;
  value: string;
}

export interface PlanFeature {
  text: string;
}

export interface Plan {
  id: string;
  name: string;
  tagline: string;
  quarterlyPrice: number;
  annualPrice: number;
  quarterlyPlanId: string;
  annualPlanId: string;
  quarterlySavings?: string;
  annualSavings: string;
  starCoinsDiscount: string;
  quarterlyMajorFeatures: MajorFeature[];
  annualMajorFeatures: MajorFeature[];
  quarterlyFeatures: PlanFeature[];
  annualFeatures: PlanFeature[];
  previewUrl: string;
  isPopular?: boolean;
}

export type PlanBillingPeriod = 'quarterly' | 'annually';

export type MembershipPlanKey = 'free' | 'economy' | 'premium' | 'elite';

export const PLAN_TAF_PERCENT: Record<MembershipPlanKey, number> = {
  free: 25,
  economy: 20,
  premium: 15,
  elite: 10,
};

/** Maps API subscription_name (e.g. "Premium Yearly", "ECO-YEARLY") to plan tier. */
export function subscriptionNameToPlanKey(
  subscriptionName: string | null | undefined,
): MembershipPlanKey {
  const n = String(subscriptionName ?? '').toLowerCase().trim();
  if (!n || n === 'free' || n.includes('free') || n.includes('limited')) return 'free';
  if (n.includes('elite')) return 'elite';
  if (n.includes('premium') || n.startsWith('prem')) return 'premium';
  if (n.includes('economy') || n.startsWith('eco')) return 'economy';
  return 'free';
}

export function resolveMembershipPlanKey(subscriptions?: {
  subscription_name?: string | null;
  subscription_plan?: string | null;
} | null): MembershipPlanKey {
  const fromName = subscriptionNameToPlanKey(subscriptions?.subscription_name);
  if (fromName !== 'free') return fromName;
  return subscriptionNameToPlanKey(subscriptions?.subscription_plan);
}

export function getTafPercentForSubscription(
  subscriptionName: string | null | undefined,
): number {
  return PLAN_TAF_PERCENT[subscriptionNameToPlanKey(subscriptionName)];
}

export function getTafPercentForSubscriptions(
  subscriptions?: {
    subscription_name?: string | null;
    subscription_plan?: string | null;
  } | null,
): number {
  return PLAN_TAF_PERCENT[resolveMembershipPlanKey(subscriptions)];
}

const SINGER_LIVE_BAND_CATEGORY_NAMES = new Set(['SINGER', 'LIVE BAND']);

/** Singer & Live Band use higher Razorpay tiers (env `*_ECONOMY1` plan IDs). Not applied on iOS. */
export function isSingerOrLiveBandCategory(categoryName?: string | null): boolean {
  const normalized = String(categoryName ?? '')
    .trim()
    .toUpperCase();
  return SINGER_LIVE_BAND_CATEGORY_NAMES.has(normalized);
}

const SINGER_LIVE_BAND_RAZORPAY_PRICES: Record<
  'economy' | 'premium' | 'elite',
  { quarterly: number; annual: number }
> = {
  economy: { quarterly: 2999, annual: 7499 },
  premium: { quarterly: 7499, annual: 19999 },
  elite: { quarterly: 19999, annual: 49999 },
};

const SINGER_LIVE_BAND_PLAN_IDS: Record<
  'economy' | 'premium' | 'elite',
  { quarterly: string; annual: string }
> = {
  economy: {
    quarterly: import.meta.env.VITE_PLAN_QUARTERLY_ECONOMY1 as string,
    annual: import.meta.env.VITE_PLAN_YEARLY_ECONOMY1 as string,
  },
  premium: {
    quarterly: import.meta.env.VITE_PLAN_QUARTERLY_PREMIUM1 as string,
    annual: import.meta.env.VITE_PLAN_YEARLY_PREMIUM1 as string,
  },
  elite: {
    quarterly: import.meta.env.VITE_PLAN_QUARTERLY_ELITE1 as string,
    annual: import.meta.env.VITE_PLAN_YEARLY_ELITE1 as string,
  },
};

/**
 * Plans for the current artist category. Singer / Live Band get higher INR + Razorpay IDs on non-iOS only.
 */
export function getMembershipPlansForCategory(
  categoryName?: string | null,
): Plan[] {
  if (isNativeIOSApp() || !isSingerOrLiveBandCategory(categoryName)) {
    return MEMBERSHIP_PLANS;
  }

  return MEMBERSHIP_PLANS.map((plan) => {
    const tier = plan.id as keyof typeof SINGER_LIVE_BAND_RAZORPAY_PRICES;
    const prices = SINGER_LIVE_BAND_RAZORPAY_PRICES[tier];
    const planIds = SINGER_LIVE_BAND_PLAN_IDS[tier];
    if (!prices || !planIds) return plan;

    return {
      ...plan,
      quarterlyPrice: prices.quarterly,
      annualPrice: prices.annual,
      quarterlyPlanId: planIds.quarterly,
      annualPlanId: planIds.annual,
    };
  });
}

/** EMI per month (₹) for Razorpay checkout; null when EMI is not offered for the plan/period. */
export function getPlanEmiAmount(
  plan: Plan,
  billingPeriod: PlanBillingPeriod,
): number | null {
  const price =
    billingPeriod === 'annually' ? plan.annualPrice : plan.quarterlyPrice;

  if (billingPeriod === 'quarterly' && plan.id === 'economy') {
    return Math.round(price / 11);
  }

  return Math.round(price / 28.5);
}

/**
 * Display prices on native iOS (App Store / RevenueCat).
 * Android and web use `quarterlyPrice` / `annualPrice` on each plan (Razorpay).
 */
const IOS_DISPLAY_PRICES: Record<
  'economy' | 'premium' | 'elite',
  { quarterly: number; annual: number }
> = {
  economy: { quarterly: 1499, annual: 3499 },
  premium: { quarterly: 3499, annual: 9999 },
  elite: { quarterly: 8999, annual: 24999 },
};

const IOS_SINGER_LIVE_BAND_DISPLAY_PRICES: Record<
  'economy' | 'premium' | 'elite',
  { quarterly: number; annual: number }
> = {
  economy: { quarterly: 3499, annual: 8999 },
  premium: { quarterly: 8999, annual: 23999 },
  elite: { quarterly: 23999, annual: 59999 },
};

/** Plan price for UI (₹). iOS native app uses App Store display amounts; all other platforms use Razorpay prices. */
export function getPlanDisplayPrice(
  plan: Plan,
  period: PlanBillingPeriod,
  categoryName?: string | null,
): number {
  if (isNativeIOSApp()) {
    const iosDisplayPrices = isSingerOrLiveBandCategory(categoryName)
      ? IOS_SINGER_LIVE_BAND_DISPLAY_PRICES
      : IOS_DISPLAY_PRICES;
    const iosPrices = iosDisplayPrices[plan.id as keyof typeof iosDisplayPrices];
    if (iosPrices) {
      return period === 'annually' ? iosPrices.annual : iosPrices.quarterly;
    }
  }
  return period === 'annually' ? plan.annualPrice : plan.quarterlyPrice;
}

export const MEMBERSHIP_PLANS: Plan[] = [
  {
    id: 'economy',
    name: 'Economy',
    tagline: 'Take off with your talent!',
    quarterlyPrice: 1199,
    annualPrice: 2999,
    quarterlyPlanId: import.meta.env.VITE_PLAN_QUARTERLY_ECONOMY as string,
    annualPlanId: import.meta.env.VITE_PLAN_YEARLY_ECONOMY as string,
    annualSavings: 'Save 38%',
    starCoinsDiscount: '25% Discount on StarCoins',
    previewUrl: 'https://starclinch.com/preview/profile/economy',
    quarterlyMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '4 Images, 2 Videos' },
      { icon: Clock, label: 'Payment Time', value: '15 Days' },
      { icon: Phone, label: 'Support', value: 'Dashboard Only' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '2 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '20%' },
      { icon: Coins, label: 'StarCoins', value: '25% Discount' },
    ],
    annualMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '4 Images, 2 Videos' },
      { icon: Clock, label: 'Payment Time', value: '15 Days' },
      { icon: Phone, label: 'Support', value: 'Dashboard Only' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '2 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '20%' },
      { icon: Coins, label: 'StarCoins', value: '3000' },
    ],
    quarterlyFeatures: [
      { text: 'Get Free StarCoins' },
      { text: 'Basic Statistics' },
      { text: 'Agency Can Handle Multiple Artists Profile' },
      { text: 'Suggest Your Profile In Search Bar (Once In Quarter)' },
      { text: 'Get Pitch Notification Only On Dashboard' },
      { text: 'Get Pitch Notification After 30 Mins Of Pitch Post' },
      { text: '30 days to publish my website' },
      { text: 'Website Will Be Only In 2 Languages (English & Hindi)' },
      { text: 'Advance UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
    ],
    annualFeatures: [
      { text: 'Get Free StarCoins' },
      { text: 'Basic Statistics' },
      { text: 'Agency Can Handle Multiple Artists Profile' },
      { text: 'Suggest Your Profile In Search Bar (Once In Quarter)' },
      { text: 'Get Pitch Notification Only On Dashboard' },
      { text: 'Get Pitch Notification After 30 Mins Of Pitch Post' },
      { text: '30 days to publish my website' },
      { text: 'Website Will Be Only In 2 Languages (English & Hindi)' },
      { text: 'Advance UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
    ],
  },
  {
    id: 'premium',
    name: 'Premium',
    tagline: 'Showcase and shine with extra perks!',
    quarterlyPrice: 2999,
    annualPrice: 7999,
    quarterlyPlanId: import.meta.env.VITE_PLAN_QUARTERLY_PREMIUM as string,
    annualPlanId: import.meta.env.VITE_PLAN_YEARLY_PREMIUM as string,
    annualSavings: 'Save 33%',
    starCoinsDiscount: '50% Discount on StarCoins',
    previewUrl: 'https://starclinch.com/preview/profile/premium',
    isPopular: true,
    quarterlyMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '8 Images, 4 Videos' },
      { icon: Clock, label: 'Payment Time', value: '7 Days' },
      { icon: Phone, label: 'Support', value: 'Call in 48 hrs' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '5 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '15%' },
      { icon: Coins, label: 'StarCoins', value: '50% Discount' },
    ],
    annualMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '8 Images, 4 Videos' },
      { icon: Clock, label: 'Payment Time', value: '7 Days' },
      { icon: Phone, label: 'Support', value: 'Call in 48 hrs' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '5 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '15%' },
      { icon: Coins, label: 'StarCoins', value: '8000' },
    ],
    quarterlyFeatures: [
      { text: 'Feature Your Profile On Our Home Page (Once a month)' },
      { text: 'Get Starclinch Verified Badge' },
      { text: 'Get In The Top Of Your Category On First Page (Once In A Month)' },
      { text: 'Get Free StarCoins' },
      { text: 'Advance AI Stats (With AI, Get To Know Where You Are Lacking Behind From Your Top Rated Artists)' },
      { text: 'Agency Can Handle Multiple Artists Profiles' },
      { text: 'Suggest Your Profile In Search Bar (Once In A Month)' },
      { text: 'Get Pitch Notification Only On Dashboard + SMS' },
      { text: 'Get Pitch Notification After 15 Mins' },
      { text: '15 days to publish my website' },
      { text: 'Website Will Be Only In 3 Languages (English, Hindi, 1 Optional)' },
      { text: 'Pro UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Flag Reviews (10 Per Year)' },
      { text: 'Remove Ad Banners From My Website' },
      // { text: 'Display My Ad Banner (Category Page) Once per month' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
    ],
    annualFeatures: [
      { text: 'Feature Your Profile On Our Home Page (Once In Month)' },
      { text: 'Get Starclinch Verified Badge' },
      { text: 'Get In The Top Of Your Category On First Page (Once In A Month)' },
      { text: 'Get Free StarCoins' },
      { text: 'Advance AI Stats (With AI, Get To Know Where You Are Lacking Behind From Your Top Rated Artists)' },
      { text: 'Agency Can Handle Multiple Artists Profiles' },
      { text: 'Suggest Your Profile In Search Bar (Once In A Month)' },
      { text: 'Get Pitch Notification Only On Dashboard + SMS' },
      { text: 'Get Pitch Notification After 15 Mins' },
      { text: '15 days to publish my website' },
      { text: 'Website Will Be Only In 3 Languages (English, Hindi, 1 Optional)' },
      { text: 'Pro UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Flag Reviews (10 Per Year)' },
      { text: 'Remove Ad Banners From My Website' },
      // { text: 'Display My Ad Banner (Category Page) Once per month' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
    ],
  },
  {
    id: 'elite',
    name: 'Elite',
    tagline: 'Soar above with exclusive VIP benefits!',
    quarterlyPrice: 7499,
    annualPrice: 19999,
    quarterlyPlanId: import.meta.env.VITE_PLAN_QUARTERLY_ELITE as string,
    annualPlanId: import.meta.env.VITE_PLAN_YEARLY_ELITE as string,
    annualSavings: 'Save 33%',
    starCoinsDiscount: '75% Discount on StarCoins',
    previewUrl: 'https://starclinch.com/preview/profile/elite',
    quarterlyMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '10 Images, 5 Videos' },
      { icon: Clock, label: 'Payment Time', value: '2 Days' },
      { icon: Phone, label: 'Support', value: 'Call in 24hrs' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '10 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '10%' },
      { icon: Coins, label: 'StarCoins', value: '75% Discount' },
    ],
    annualMajorFeatures: [
      { icon: Upload, label: 'Media Uploads', value: '10 Images, 5 Videos' },
      { icon: Clock, label: 'Payment Time', value: '2 Days' },
      { icon: Phone, label: 'Support', value: 'Call in 24hrs' },
      { icon: TrendingUp, label: 'Min Pitches/Month', value: '10 Pitches' },
      { icon: Shield, label: 'TAF Fee', value: '10%' },
      { icon: Coins, label: 'StarCoins', value: '20,000' },
    ],
    quarterlyFeatures: [
      { text: 'Profile Displayed On Homepage (Once per month)' },
      { text: 'Get Starclinch Verified Badge' },
      { text: 'Get In The Top Of Your Category On First Page (Once In A Week)' },
      { text: 'Advance AI Stats (With AI, Get To Know Where You Are Lacking Behind From Your Top Rated Artists)' },
      { text: 'Agency Can Handle Multiple Artists Profiles' },
      { text: 'Suggest Your Profile In Search Bar (Once In A Week)' },
      { text: 'Get Pitch Notifications Only On Dashboard + SMS + Whatsapp' },
      { text: 'Get Instant Pitch Notifications' },
      { text: '5 days to publish my website' },
      { text: 'Website Will Be In 10 Languages (English, Hindi, And 8 Other)' },
      { text: 'Royal UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Flag Reviews (Unlimited)' },
      { text: 'Remove Ad Banners From My Website' },
      { text: "Display My Ad Banner (Others' Website)" },
      { text: 'Display My Ad Banner (Category Page) Once per week' },
      { text: 'Profile Featured In Newsletters (Once Per Month)' },
      { text: 'Reels Collab With StarClinch (Once Per Year)' },
      { text: 'Story Re-Sharing By StarClinch (Once Per Year)' },
      { text: 'Story Added To Highlights By StarClinch (Once Per Year)' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
      { text: 'Blog Post' },
    ],
    annualFeatures: [
      { text: 'Profile Displayed On Homepage (Once per month)' },
      { text: 'Get Starclinch Verified Badge' },
      { text: 'Get In The Top Of Your Category On First Page (Once In A Week)' },
      { text: 'Get Free StarCoins' },
      { text: 'Advance AI Stats (With AI, Get To Know Where You Are Lacking Behind From Your Top Rated Artists)' },
      { text: 'Agency Can Handle Multiple Artists Profiles' },
      { text: 'Suggest Your Profile In Search Bar (Once In A Week)' },
      { text: 'Get Pitch Notifications Only On Dashboard + SMS + Whatsapp' },
      { text: 'Get Instant Pitch Notifications' },
      { text: '5 days to publish my website' },
      { text: 'Website Will Be In 10 Languages (English, Hindi, And 8 Other)' },
      { text: 'Royal UI/UX Design' },
      { text: 'Custom FAQS' },
      { text: 'Flag Reviews (Unlimited)' },
      { text: 'Remove Ad Banners From My Website' },
      { text: "Display My Ad Banner (Others' Website)" },
      { text: 'Display My Ad Banner (Category Page) Once per week' },
      { text: 'Profile Featured In Newsletters (Once Per Month)' },
      { text: 'Reels Collab With StarClinch (Once Per Year)' },
      { text: 'Story Re-Sharing By StarClinch (Once Per Year)' },
      { text: 'Story Added To Highlights By StarClinch (Once Per Year)' },
      { text: 'Receive Payment Online' },
      { text: 'Stay 100% Tax Compliant' },
      { text: 'Blog Post' },
    ],
  },
];
